/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { DataTable } from '@/components/CustomTable'
import { toast } from 'sonner'
import { Pencil, PlusCircle, MinusCircle } from 'lucide-react'
import {
  useItemQuery,
  useItemMovesQueryQ,
  useUpdateItemMutation,
  usePurchaseStockMutation,
  useAdjustStockMutation,
} from '@/hooks/useItems'
import { useCategorySelectQuery } from '@/hooks/useCategories'
import { formatDateFR, formatCFA } from '@/lib/utils'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useContainerSelectQuery } from '@/hooks/useContainers'

export default function ItemDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: item, status } = useItemQuery(id!, !!id)
  const { data: movesResp, isFetching: movesFetching } = useItemMovesQueryQ(
    id!,
    !!id
  )
  const { data: cats } = useCategorySelectQuery(true)
  const { data: containerOptions } = useContainerSelectQuery(undefined, true)

  const updateMut = useUpdateItemMutation(id!)
  const purchaseMut = usePurchaseStockMutation()
  const adjustMut = useAdjustStockMutation()

  // ----- Modales
  const [editOpen, setEditOpen] = useState(false)
  const [purchaseOpen, setPurchaseOpen] = useState(false)
  const [adjustOpen, setAdjustOpen] = useState(false)

  // ----- Forms
  const [edit, setEdit] = useState({
    name: '',
    sku: '',
    unitPrice: '',
    packaging: '',
    categoryId: 'NONE', // sentinelle pour "aucune"
  })
  const [purchase, setPurchase] = useState({
    quantity: '',
    unitPrice: '',
    note: '',
    occurredAt: '',
    containerId: 'NONE',
  })
  const [adjust, setAdjust] = useState({
    quantityDelta: '',
    note: '',
    occurredAt: '',
    containerId: 'NONE',
  })

  useEffect(() => {
    if (item) {
      setEdit({
        name: item.name ?? '',
        sku: item.sku ?? '',
        unitPrice: String(item.unitPrice ?? ''),
        packaging: item.packaging ?? '',
        categoryId: item.category?.id ?? 'NONE',
      })
    }
  }, [item])

  const lines = useMemo(() => movesResp?.moves ?? [], [movesResp])

  // ----- Libellés mouvements
  const typeLabel: Record<string, string> = {
    PURCHASE: 'Entrée (achat)',
    ADJUST: 'Ajustement',
    SALE: 'Vente',
    RETURN: 'Retour',
  }

  const columns = useMemo(() => {
    return [
      {
        accessorKey: 'occurredAt',
        header: 'Date',
        cell: ({ row }: any) =>
          formatDateFR(row.original.occurredAt, { showTime: 'auto' }),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }: any) => {
          const t = row.original.type as keyof typeof typeLabel
          return (
            <span className='inline-flex items-center rounded-full border px-2 py-0.5 text-xs'>
              {typeLabel[t] ?? row.original.type}
            </span>
          )
        },
      },
      {
        accessorKey: 'quantity',
        header: 'Qté',
        cell: ({ row }: any) => (
          <span className='tabular-nums'>{row.original.quantity}</span>
        ),
      },
      {
        accessorKey: 'unitPrice',
        header: 'PU',
        cell: ({ row }: any) => (
          <span className='tabular-nums'>
            {formatCFA(row.original.unitPrice)}
          </span>
        ),
      },
      {
        accessorKey: 'container',
        header: 'Conteneur',
        cell: ({ row }: any) => row.original.container?.name ?? '—',
      },
      {
        accessorKey: 'note',
        header: 'Note',
        cell: ({ row }: any) => row.original.note ?? '—',
      },
    ] as const
  }, [typeLabel])

  // ----- Submit handlers
  const submitEdit = async () => {
    if (!item) return
    const name = edit.name.trim()
    const sku = edit.sku.trim() || undefined
    const packaging = edit.packaging.trim() || undefined
    const unitPrice = parseFloat((edit.unitPrice || '').replace(',', '.'))
    const categoryId = edit.categoryId === 'NONE' ? undefined : edit.categoryId

    if (!name) return toast.error('Nom requis')
    if (!isFinite(unitPrice) || unitPrice <= 0) {
      return toast.error('Prix unitaire invalide', {
        description: 'Nombre > 0',
      })
    }

    try {
      await updateMut.mutateAsync({
        name,
        sku,
        unitPrice,
        packaging,
        categoryId,
      })
      toast.success('Produit modifié')
      setEditOpen(false)
    } catch (e: any) {
      toast.error('Échec modification', {
        description: e?.response?.data?.message,
      })
    }
  }

  const submitPurchase = async () => {
    if (!item) return
    const quantity = parseInt(purchase.quantity, 10)
    const unitPrice = parseFloat((purchase.unitPrice || '').replace(',', '.'))

    if (!Number.isInteger(quantity) || quantity <= 0) {
      return toast.error('Quantité invalide', { description: 'Entier ≥ 1' })
    }
    if (!isFinite(unitPrice) || unitPrice <= 0) {
      return toast.error('Prix d’achat invalide', { description: 'Nombre > 0' })
    }

    try {
      await purchaseMut.mutateAsync({
        itemId: item.id,
        quantity,
        unitPrice,
        note: purchase.note || undefined,
        occurredAt: purchase.occurredAt || undefined,
        containerId:
          purchase.containerId !== 'NONE' ? purchase.containerId : undefined,
      })
      toast.success('Entrée stock enregistrée')
      setPurchaseOpen(false)
      setPurchase({
        quantity: '',
        unitPrice: '',
        note: '',
        occurredAt: '',
        containerId: 'NONE',
      })
    } catch (e: any) {
      toast.error('Échec entrée stock', {
        description: e?.response?.data?.message,
      })
    }
  }

  const submitAdjust = async () => {
    if (!item) return
    const quantityDelta = parseInt(adjust.quantityDelta, 10)
    if (!Number.isInteger(quantityDelta) || quantityDelta === 0) {
      return toast.error('Delta invalide', {
        description: 'Entier non nul (positif ou négatif)',
      })
    }

    try {
      await adjustMut.mutateAsync({
        itemId: item.id,
        quantityDelta,
        note: adjust.note || undefined,
        occurredAt: adjust.occurredAt || undefined,
        containerId:
          adjust.containerId !== 'NONE' ? adjust.containerId : undefined,
      })
      toast.success('Ajustement stock enregistré')
      setAdjustOpen(false)
      setAdjust({
        quantityDelta: '',
        note: '',
        occurredAt: '',
        containerId: 'NONE',
      })
    } catch (e: any) {
      toast.error('Échec ajustement', {
        description: e?.response?.data?.message,
      })
    }
  }

  if (status === 'pending') return <Loading />
  if (!item) return <div className='p-4'>Article introuvable.</div>

  return (
    <div className='space-y-6'>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to='/items'>Produits</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{item.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className='flex items-center flex-col gap-y-4 sm:flex-row justify-between'>
        <h2 className='text-2xl font-semibold'>
          {item.name}{' '}
          <span className='text-sm text-muted-foreground'>
            #{item.id.slice(0, 8)}
          </span>
        </h2>
        <div
          className={`grid gap-4 grid-cols-2 ${
            item.type === 'SERVICE' ? 'sm:grid-cols-1' : 'sm:grid-cols-3'
          }`}
        >
          <Button variant='outline' onClick={() => setEditOpen(true)}>
            <Pencil className='h-4 w-4 mr-2' /> Modifier
          </Button>
          {item.type !== 'SERVICE' && (
            <>
              <Button variant='outline' onClick={() => setPurchaseOpen(true)}>
                <PlusCircle className='h-4 w-4 mr-2' /> Entrée stock
              </Button>
              <Button variant='outline' onClick={() => setAdjustOpen(true)}>
                <MinusCircle className='h-4 w-4 mr-2' /> Ajuster stock
              </Button>
            </>
          )}
        </div>
      </div>

      <div className='grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'>
        <Info label='SKU' value={item.sku ?? '—'} />
        <Info label='Catégorie' value={item.category?.name ?? '—'} />
        <Info label='Prix unitaire' value={formatCFA(item.unitPrice)} />
        <Info
          label='Stock'
          value={item.type === 'SERVICE' ? '—' : String(item.stockOnHand ?? 0)}
        />
      </div>

      {item.type !== 'SERVICE' && (
        <>
          <div className='flex items-center justify-between'>
            <h3 className='text-lg font-semibold'>Mouvements de stock</h3>
            <div className='text-xs text-muted-foreground'>
              {movesFetching ? 'Actualisation…' : null}
            </div>
          </div>

          <DataTable<(typeof lines)[number], unknown>
            columns={columns as any}
            data={lines}
          />
        </>
      )}

      {/* ----- Modale ÉDITER ----- */}
      <AppDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title='Modifier le produit'
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submitEdit()
        }}
        footer={
          <ModalActions
            onConfirm={submitEdit}
            confirmLabel='Valider'
            confirmLoading={updateMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <Field label='Nom'>
            <Input
              value={edit.name}
              onChange={(e) => setEdit((f) => ({ ...f, name: e.target.value }))}
              placeholder='Ex: Coca 50cl'
            />
          </Field>

          <Field label='SKU (optionnel)'>
            <Input
              value={edit.sku}
              onChange={(e) => setEdit((f) => ({ ...f, sku: e.target.value }))}
              placeholder='Code interne / barre'
            />
          </Field>

          <Field label='Prix unitaire (CFA)'>
            <Input
              inputMode='decimal'
              type='number'
              step='0.01'
              min={0}
              value={edit.unitPrice}
              onChange={(e) =>
                setEdit((f) => ({ ...f, unitPrice: e.target.value }))
              }
              placeholder='Ex: 500'
            />
          </Field>

          <Field label='Catégorie (optionnel)'>
            <Select
              value={edit.categoryId || 'NONE'} // jamais ''
              onValueChange={(v) => setEdit((f) => ({ ...f, categoryId: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NONE'>— Aucune —</SelectItem>
                {(cats ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label='Conditionnement (optionnel)'>
            <Input
              value={edit.packaging}
              onChange={(e) =>
                setEdit((f) => ({ ...f, packaging: e.target.value }))
              }
              placeholder='Ex: carton de 24'
            />
          </Field>
        </div>
      </AppDialog>

      {/* ----- Modale ENTRÉE STOCK ----- */}
      <AppDialog
        open={purchaseOpen}
        onOpenChange={setPurchaseOpen}
        title='Entrée stock (achat)'
        description={`Produit : ${item.name}`}
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submitPurchase()
        }}
        footer={
          <ModalActions
            onConfirm={submitPurchase}
            confirmLabel='Valider'
            confirmLoading={purchaseMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <Field label='Quantité'>
            <Input
              type='number'
              inputMode='numeric'
              step='1'
              min={1}
              value={purchase.quantity}
              onChange={(e) =>
                setPurchase((f) => ({ ...f, quantity: e.target.value }))
              }
              placeholder='Ex: 24'
              autoFocus
            />
          </Field>
          <Field label='Prix d’achat unitaire (CFA)'>
            <Input
              type='number'
              inputMode='decimal'
              step='0.01'
              min={0}
              value={purchase.unitPrice}
              onChange={(e) =>
                setPurchase((f) => ({ ...f, unitPrice: e.target.value }))
              }
              placeholder='Ex: 350'
            />
          </Field>
          <Field label='Conteneur (optionnel)'>
            <Select
              value={purchase.containerId || 'NONE'}
              onValueChange={(v) =>
                setPurchase((f) => ({ ...f, containerId: v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NONE'>— Aucun —</SelectItem>
                {(containerOptions ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.isDefault ? `★ ${c.name}` : c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label='Note (optionnel)'>
            <Input
              value={purchase.note}
              onChange={(e) =>
                setPurchase((f) => ({ ...f, note: e.target.value }))
              }
              placeholder='Référence facture fournisseur…'
            />
          </Field>
          <Field label='Date (optionnel)'>
            <Input
              type='datetime-local'
              value={purchase.occurredAt}
              onChange={(e) =>
                setPurchase((f) => ({ ...f, occurredAt: e.target.value }))
              }
            />
          </Field>
        </div>
      </AppDialog>

      {/* ----- Modale AJUSTEMENT ----- */}
      <AppDialog
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        title='Ajustement stock'
        description={`Produit : ${item.name} — positif = entrée, négatif = sortie`}
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submitAdjust()
        }}
        footer={
          <ModalActions
            onConfirm={submitAdjust}
            confirmLabel='Valider'
            confirmLoading={adjustMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <Field label='Delta (peut être négatif)'>
            <Input
              type='number'
              inputMode='numeric'
              step='1'
              value={adjust.quantityDelta}
              onChange={(e) =>
                setAdjust((f) => ({ ...f, quantityDelta: e.target.value }))
              }
              placeholder='Ex: -2 ou 5'
              autoFocus
            />
          </Field>
          <Field label='Note (optionnel)'>
            <Input
              value={adjust.note}
              onChange={(e) =>
                setAdjust((f) => ({ ...f, note: e.target.value }))
              }
              placeholder='Casse, inventaire…'
            />
          </Field>
          <Field label='Conteneur (optionnel)'>
            <Select
              value={adjust.containerId || 'NONE'}
              onValueChange={(v) =>
                setAdjust((f) => ({ ...f, containerId: v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NONE'>— Aucun —</SelectItem>
                {(containerOptions ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.isDefault ? `★ ${c.name}` : c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label='Date (optionnel)'>
            <Input
              type='datetime-local'
              value={adjust.occurredAt}
              onChange={(e) =>
                setAdjust((f) => ({ ...f, occurredAt: e.target.value }))
              }
            />
          </Field>
        </div>
      </AppDialog>
    </div>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-lg border p-3'>
      <div className='text-xs text-muted-foreground'>{label}</div>
      <div className='mt-1 font-medium'>{value}</div>
    </div>
  )
}
function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className='grid gap-1.5'>
      <Label>{label}</Label>
      {children}
    </div>
  )
}
